import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
  CircularProgress
} from '@mui/material';
import { Participant, SiteSettings, generateMultipleTemplatePreviews, generateMultipleTemplatePreviewsAsync, PreviewData } from '../utils/templateRenderer';
import { Event } from '../types/Event';

interface WhatsAppTemplate {
  id: string;
  name: string;
  description: string;
}

// Simple template getter for basic template info
const getTemplateSynx = (templateId: string): WhatsAppTemplate | null => {
  const templates: Record<string, WhatsAppTemplate> = {
    'join-event': { id: 'join-event', name: '報名確認', description: '參加者報名活動後的確認訊息' },
    'payment': { id: 'payment', name: '付款通知', description: '發送付款連結給參加者' },
    'broadcast': { id: 'broadcast', name: '廣播訊息', description: '發送廣播訊息給所有參加者' },
    'reminder': { id: 'reminder', name: '活動提醒', description: '使用廣播模板發送活動提醒' }
  };
  return templates[templateId] || null;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  templateId: string;
  participants: Participant[];
  event: Event;
  siteSettings: SiteSettings;
  siteName: string;
  customMessage?: string;
  title?: string;
  confirmButtonText?: string;
  isLoading?: boolean;
  showConfirmButton?: boolean;
}

const WhatsAppMessagePreview: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  templateId,
  participants,
  event,
  siteSettings,
  siteName,
  customMessage,
  title,
  confirmButtonText = '發送訊息',
  isLoading = false,
  showConfirmButton = true
}) => {
  const template = getTemplateSynx(templateId);
  const [previews, setPreviews] = useState<Array<{ participant: Participant; preview: string; isValid: boolean; missingVariables: string[] }>>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const getPreviewTitle = () => {
    if (title) return title;
    return `${template?.name || '訊息'}預覽`;
  };

  const generatePreviews = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const previewData: PreviewData = {
        templateId,
        participants,
        event,
        siteSettings,
        siteName,
        customMessage
      };

      let generatedPreviews;
      
      // Use async version for reminder template without custom message
      if (templateId === 'reminder' && !customMessage) {
        generatedPreviews = await generateMultipleTemplatePreviewsAsync(previewData);
      } else {
        generatedPreviews = generateMultipleTemplatePreviews(previewData);
      }
      
      setPreviews(generatedPreviews);
    } catch (error) {
      console.error('Error generating previews:', error);
      setPreviews([]);
    } finally {
      setPreviewLoading(false);
    }
  }, [templateId, participants, event, siteSettings, siteName, customMessage]);

  // Generate previews when modal opens or data changes
  useEffect(() => {
    if (open && participants.length > 0) {
      generatePreviews();
    }
  }, [open, participants.length, generatePreviews]);

  // Check for potential issues
  const hasIssues = () => {
    if (templateId === 'payment' && !siteSettings.paymentLink) return true;
    if (templateId === 'broadcast' && !customMessage?.trim()) return true;
    // For reminder template, no custom message is fine - it will use default template
    if (previews.length > 0 && !previews[0].isValid) return true;
    return false;
  };

  const getIssueMessage = () => {
    if (templateId === 'payment' && !siteSettings.paymentLink) {
      return '付款連結尚未設定，請先到群組管理設定付款連結';
    }
    if (templateId === 'broadcast' && !customMessage?.trim()) {
      return '請輸入廣播訊息內容';
    }
    if (previews.length > 0 && !previews[0].isValid) {
      return `缺少必要資訊: ${previews[0].missingVariables.join(', ')}`;
    }
    return '';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{getPreviewTitle()}</DialogTitle>
      <DialogContent>
        {/* Issue Alert */}
        {hasIssues() && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getIssueMessage()}
          </Alert>
        )}

        {/* Recipients Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            發送對象
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`共 ${participants.length} 位參加者`} color="primary" variant="outlined" />
            {templateId === 'payment' && (
              <Chip label="僅發送給未付款者" color="warning" variant="outlined" />
            )}
          </Box>
          
          <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {participants.map((participant, index) => (
              <ListItem key={participant._id || index}>
                <ListItemText 
                  primary={participant.name} 
                  secondary={`${participant.phone} • ${participant.spots} 人 • ${participant.paid ? '已付款' : '未付款'}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Message Preview */}
        {previewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>正在生成預覽...</Typography>
          </Box>
        ) : previews.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              訊息範例：
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: '#f5f5f5', 
                fontFamily: 'monospace',
                whiteSpace: 'pre-line',
                mb: 2,
                fontSize: '0.9rem'
              }}
            >
              {previews[0]?.preview || '無法生成預覽'}
            </Paper>

            {participants.length > 1 && (
              <Typography variant="body2" color="text.secondary">
                * 每位參加者會收到個人化的訊息（姓名、電話和相關資訊會相應調整）
              </Typography>
            )}
          </Box>
        ) : (
          <Alert severity="error" sx={{ mt: 2 }}>
            無法生成訊息預覽，請檢查資料是否完整
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          取消
        </Button>
        {showConfirmButton && (
          <Button 
            onClick={onConfirm} 
            variant="contained" 
            color="primary" 
            disabled={isLoading || hasIssues() || participants.length === 0 || previewLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          >
            {isLoading ? '發送中...' : confirmButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WhatsAppMessagePreview; 